import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {animate, sequence, style, transition, trigger} from '@angular/animations';
import {FormBuilder, FormControl} from "@angular/forms";
import {CdkVirtualForOf} from '@angular/cdk/scrolling';

import {filter, take} from 'rxjs';

import {PanelNavigationService} from '@commons/services/navigation/panel-navigation.service';

import {Driver} from '../../../commons/models/domain/Driver';

import {WorkerHttpService} from "@layout/driver/http-services/worker-http.service";
import {WorkerService} from "@layout/driver/services/worker.service";

import {WorkerModel} from "@shared/models/worker.model";
import {BaseComponent} from "@shared/components/base-component";
import {NewNavigationService} from "@shared/services/new-navigation.service";
import {NewDataStoreService} from "@shared/services/new-data-store.service";
import {AuthenticationService} from "@shared/services/authentication/authentication.service";

@Component({
  selector: 'wm-driver-list-lite',
  templateUrl: './driver-list-lite.component.html',
  styleUrls: ['./driver-list-lite.component.less'],
  animations: [
    trigger('openClose', [
      transition(":enter", [style({transform: 'translateX(-264px)'}), animate('0.1s')]),
      transition(":leave", [sequence([style({left: '56px', 'z-index': 0}), animate('0.1s', style({left: '-264px'}))])]),
    ])
  ]
})
export class DriverListLiteComponent extends BaseComponent implements OnInit, OnDestroy {

  @ViewChild(CdkVirtualForOf)
  set setWorkersList(workersList: CdkVirtualForOf<Driver>) {
    if (workersList && !this.isSubToWorkersListViewChange) {
      this.isSubToWorkersListViewChange = true;
      this.subToWorkersListViewChange(workersList);
    }
  }

  selectedDriverId?: number;
  isVisible = false;
  allDrivers?: Driver[];
  displayedDrivers?: Driver[];
  allArchiveWorkers: Driver[] = [];
  displayedArchiveWorkers: Driver[] = [];
  searchCtrl = new FormControl('');
  form = this.formBuilder.group({
    search: this.searchCtrl,
  });
  isLoadingWorkers = true;
  isLoadingArchiveDrivers = true;
  isShown = false;
  isSubToWorkersListViewChange = false;

  get isNewDriverButtonVisible(): boolean {
    return this.workerService.newUnsavedDriver == null;
  }

  get driversCount(): number {
    return this.displayedDrivers != null ? this.displayedDrivers.length : 0;
  }

  get archiveWorkersCount(): number {
    return this.displayedArchiveWorkers != null ? this.displayedArchiveWorkers.length : 0;
  }

  get hasAnyDrivers(): boolean {
    return this.allDrivers != null && this.allDrivers.length > 0;
  }

  get hasAnyArchiveDrivers(): boolean {
    return this.allArchiveWorkers != null && this.allArchiveWorkers.length === 0;
  }

  constructor(
    private panelNavigationService: PanelNavigationService,
    private driverHttpService: WorkerHttpService,
    private workerService: WorkerService,
    private formBuilder: FormBuilder,
    private newNavigationService: NewNavigationService,
    private newDataStoreService: NewDataStoreService,
    private authenticationService: AuthenticationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadWorkers();
    this.subToSearchValueChanges();
    this.subToVisibility();
    this.subToWorkerAdded();
    this.subToWorkerUpdated();
    this.subToWorkerArchived();
    this.subToWorkerUnarchived();
    this.subToWorkerDetailsPanelClosed();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  onSelect(driver: Driver): void {
    if (driver.Id && this.selectedDriverId === driver.Id) {
      this.selectedDriverId = undefined;
      this.newNavigationService.toWorkersList();
    } else {
      this.selectedDriverId = driver.Id;
      this.newNavigationService.toWorkerDetails(this.selectedDriverId);
    }
  }

  onAddDriver() {
    const newDriver = {
      Id: 0,
      FN: ' ',
      LN: ' ',
    } as Driver;
    this.allDrivers?.unshift(newDriver);
    this.displayedDrivers = this.allDrivers;
    this.refreshListAfterDriverAdd();
    this.selectedDriverId = newDriver.Id;
    this.workerService.newUnsavedDriver = newDriver;
    this.onSelect(newDriver);
  }

  onSelectedTabChanged() {
    this.newNavigationService.toWorkersList();
  }

  onClose() {
    this.newNavigationService.toMainPanel();
    this.removeNewWorker();
  }

  isItemSelected(driver: Driver): boolean {
    return this.selectedDriverId === driver.Id;
  }

  private removeNewWorker() {
    this.workerService.newUnsavedDriver = undefined;
    this.allDrivers?.splice(0, 1);
    this.refreshListAfterDriverAdd();
  }

  private subToWorkersListViewChange(workersList: CdkVirtualForOf<Driver>) {
    //wczytanie przez kontrolkę listy prawocników trwa chiwilę i był problem z pokazaniem że pracownicy jeszcze się wczytują
    //pomimo tego że kontrolka listy dostała już dane ale jeszcze ich nie wyświetliła dlatego czekam na event narysowania danych prze tą kontrolkę
    //im większa lista tym ten problem był bardziej zauważalny
    this.addToUnsubscribe(
      workersList.viewChange.subscribe(() => {
        if (this.isLoadingWorkers) {
          this.isLoadingWorkers = false;
          this.loadArchiveDrivers();
        }
      }));
  }

  private subToVisibility() {
    this.addToUnsubscribe(this.panelNavigationService.getVisibilitySubject()
      .pipe(filter(item => (
        !item.subsites
        || item.subsites.indexOf('driver-lite') !== -1
        || item.subsites.indexOf('driver-details') !== -1
      )))
      .subscribe(change => {
        if (change.id) {
          this.selectedDriverId = parseInt(change.id, 10);
        } else {
          this.selectedDriverId = undefined;
        }
        this.isVisible = change.show;
        if (this.isVisible && !this.isShown) {
          this.isShown = true;
        }
      }));
  }

  private refreshListAfterDriverAdd() {
    //obejście nie pokazywania nowego elementu na liście prze wykożystanie wirualnego scrolla
    this.searchCtrl.setValue('_');
    this.searchCtrl.setValue('');
  }

  private subToSearchValueChanges() {
    this.addToUnsubscribe(
      this.searchCtrl.valueChanges.subscribe(() => {
        if (this.searchCtrl.valid || this.searchCtrl.value?.length === 0) {
          const filter = (this.searchCtrl.value ?? '').toLowerCase();
          this.displayedDrivers = this.allDrivers?.filter(x => (x.FN + x.LN).toLowerCase().includes(filter));
          this.displayedArchiveWorkers = this.allArchiveWorkers?.filter(x => (x.FN + x.LN).toLowerCase().includes(filter));
        }
      }));
  }

  private convertWorkerModelToDriver(x: WorkerModel[]): Driver[] {
    return x.map(w => <Driver>{
      Id: w.Id,
      FN: w?.FirstName,
      LN: w?.LastName,
      Pn: w.PhoneNumber,
      CN: w.CellphoneNumber,
      IsArchive: true,
      L: w.DriverLicenseNumber,
    });
  }

  private displayWorkers(workers: Driver[]) {
    if (workers.length === 0) {
      //jeśli nie ma pracowników to kontrolka listy się nie narysuje wiec trzeba wymusić wczytania pracowników archiwalnych
      this.isLoadingWorkers = false;
      this.loadArchiveDrivers();
    }

    this.displayedDrivers =
      this.allDrivers =
        this.workerService.allWorkers = workers.sort(this.sortDrivers);
  }

  private loadWorkers() {
    this.addToUnsubscribe(
      this.newDataStoreService.driversLoaded.subscribe(result => {
        this.displayWorkers(result);
      }));
  }

  private displayArchiveWorkers(workers: Driver[]) {
    this.displayedArchiveWorkers =
      this.allArchiveWorkers =
        this.workerService.archivedDrivers = workers.sort(this.sortDrivers);
  }

  private loadArchiveDrivers() {
    this.authenticationService.isAuthenticated$.pipe(take(1)).subscribe(isAuthenticated => {
      if (isAuthenticated && !this.authenticationService.isLoggedByToken) {
        this.driverHttpService.getArchivedWorkers().subscribe(result => {
          this.isLoadingArchiveDrivers = false;
          this.displayArchiveWorkers(this.convertWorkerModelToDriver(result));
        });
      }
    });
  }

  private sortDrivers(a: Driver, b: Driver): number {
    return (a.FN + " " + a.LN).localeCompare(b.FN + " " + b.LN);
  }

  private subToWorkerUnarchived() {
    this.workerService.unArchivedWorkerSubject.subscribe(driver => {
      const filtered = this.allArchiveWorkers!.filter(x => driver.Id !== x.Id);
      this.displayArchiveWorkers(filtered);
      this.allDrivers!.push(driver);
      this.displayWorkers(this.allDrivers!);
      this.refreshListAfterDriverAdd();
    });
  }

  private subToWorkerArchived() {
    this.workerService.archivedWorkerSubject.subscribe(driver => {
      const filtered = this.allDrivers!.filter(x => driver.Id !== x.Id);
      this.displayWorkers(filtered);
      this.allArchiveWorkers!.push(driver);
      this.displayArchiveWorkers(this.allArchiveWorkers);
      this.refreshListAfterDriverAdd();
    });
  }

  private subToWorkerAdded() {
    this.workerService.workerAddedSubject.subscribe(addedDriver => {
      const matchedDriver = this.allDrivers!.find(x => x.Id === 0);//0 to id nowo dodanego nie zapisanego pracownika
      this.copyDriverToDriver(addedDriver, matchedDriver!, true);
      this.displayWorkers(this.allDrivers!);
    });
  }

  private subToWorkerUpdated() {
    this.workerService.workerUpdatedSubject.subscribe(updatedDriver => {
      const matchedDriver = this.allDrivers!.find(x => x.Id === updatedDriver.Id);
      this.copyDriverToDriver(updatedDriver, matchedDriver!);
      this.displayWorkers(this.allDrivers!);
    });
  }

  private copyDriverToDriver(source: Driver, target: Driver, copyId = false) {
    target.FN = source.FN;
    target.LN = source.LN;
    target.Pn = source.Pn;
    target.CN = source.CN;
    target.Em = source.Em;

    if (copyId)
      target.Id = source.Id;
  }

  private subToWorkerDetailsPanelClosed() {
    this.workerService.workerDetailsPanelClosedSub$.subscribe(() => {
      this.removeNewWorker();
    });
  }
}

